/** §3.2 step 2 — MIME sniffing by magic bytes, then extension, then `file.type`. */

const EXT_MAP: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', tif: 'image/tiff', tiff: 'image/tiff', svg: 'image/svg+xml', psd: 'image/vnd.adobe.photoshop', ai: 'application/postscript', bmp: 'image/bmp', avif: 'image/avif',
  mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', mkv: 'video/x-matroska', avi: 'video/x-msvideo',
  mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac', ogg: 'audio/ogg',
  pdf: 'application/pdf', txt: 'text/plain', csv: 'text/csv', json: 'application/json', zip: 'application/zip',
};

function ascii(b: Uint8Array, start: number, len: number): string {
  let s = '';
  for (let i = start; i < Math.min(b.length, start + len); i++) s += String.fromCharCode(b[i]!);
  return s;
}
function startsWith(b: Uint8Array, bytes: number[], offset = 0): boolean {
  if (b.length < offset + bytes.length) return false;
  for (let i = 0; i < bytes.length; i++) if (b[offset + i] !== bytes[i]) return false;
  return true;
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

/** Pure sniff over the first 4 KiB; exported for tests. */
export function sniffBytes(b: Uint8Array, name: string, declaredType = ''): string {
  const ext = extensionOf(name);
  if (startsWith(b, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (ascii(b, 0, 6) === 'GIF87a' || ascii(b, 0, 6) === 'GIF89a') return 'image/gif';
  if (ascii(b, 0, 4) === 'RIFF') {
    const kind = ascii(b, 8, 4);
    if (kind === 'WEBP') return 'image/webp';
    if (kind === 'WAVE') return 'audio/wav';
    if (kind === 'AVI ') return 'video/x-msvideo';
  }
  if (ascii(b, 4, 4) === 'ftyp') {
    const brand = ascii(b, 8, 4);
    if (/^(heic|heix|hevc|mif1|msf1|heim|heis|hevm|hevs)$/.test(brand)) return brand === 'mif1' && ext === 'avif' ? 'image/avif' : 'image/heic';
    if (brand === 'avif' || brand === 'avis') return 'image/avif';
    if (brand === 'qt  ') return 'video/quicktime';
    if (brand === 'M4A ') return 'audio/mp4';
    if (ext === 'mov') return 'video/quicktime';
    return 'video/mp4';
  }
  if (startsWith(b, [0x49, 0x49, 0x2a, 0x00]) || startsWith(b, [0x4d, 0x4d, 0x00, 0x2a])) return 'image/tiff';
  if (startsWith(b, [0x1a, 0x45, 0xdf, 0xa3])) return ext === 'mkv' ? 'video/x-matroska' : 'video/webm';
  if (ascii(b, 0, 3) === 'ID3' || startsWith(b, [0xff, 0xfb]) || startsWith(b, [0xff, 0xf3]) || startsWith(b, [0xff, 0xf2])) return 'audio/mpeg';
  if (ascii(b, 0, 4) === 'fLaC') return 'audio/flac';
  if (ascii(b, 0, 4) === 'OggS') return 'audio/ogg';
  if (ascii(b, 0, 4) === '%PDF') return ext === 'ai' ? 'application/postscript' : 'application/pdf';
  if (ascii(b, 0, 4) === '8BPS') return 'image/vnd.adobe.photoshop';
  if (ascii(b, 0, 2) === 'BM' && ext === 'bmp') return 'image/bmp';
  if (ext === 'svg') {
    const head = ascii(b, 0, 1024).toLowerCase();
    if (head.includes('<svg') || head.includes('<?xml')) return 'image/svg+xml';
  }
  const byExt = EXT_MAP[ext];
  if (byExt) return byExt;
  if (declaredType) return declaredType;
  return 'application/octet-stream';
}

export async function sniffMime(file: File): Promise<string> {
  let bytes = new Uint8Array(0);
  try {
    bytes = new Uint8Array(await file.slice(0, 4096).arrayBuffer());
  } catch {
    /* unreadable → fall back to name/type */
  }
  return sniffBytes(bytes, file.name, file.type);
}

export function assetTypeOf(mime: string): 'image' | 'video' | 'audio' | 'document' | 'other' {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf' || mime === 'application/postscript' || mime.startsWith('text/')) return 'document';
  return 'other';
}

export function mimeAllowed(mime: string, prefixes: readonly string[] | undefined | null): boolean {
  if (!prefixes || prefixes.length === 0) return true;
  return prefixes.some((p) => mime.startsWith(p));
}
